import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Survey, SurveyResponse } from '../types/Survey';
import { v4 as uuidv4 } from 'uuid';

interface SurveyContextType {
  surveys: Survey[];
  addSurvey: (survey: Omit<Survey, 'id' | 'createdAt' | 'responses'>) => void;
  updateSurvey: (id: string, survey: Partial<Survey>) => void;
  deleteSurvey: (id: string) => void;
  getSurvey: (id: string) => Survey | undefined;
  addResponse: (surveyId: string, response: Omit<SurveyResponse, 'id' | 'submittedAt'>) => void;
  getResponses: (surveyId: string) => SurveyResponse[];
}

const SurveyContext = createContext<SurveyContextType | undefined>(undefined);

export const SurveyProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [surveys, setSurveys] = useState<Survey[]>([]);

  const addSurvey = (surveyData: Omit<Survey, 'id' | 'createdAt' | 'responses'>) => {
    const newSurvey: Survey = {
      ...surveyData,
      id: uuidv4(),
      createdAt: new Date(),
      responses: []
    };
    setSurveys(prev => [...prev, newSurvey]);
  };

  const updateSurvey = (id: string, updates: Partial<Survey>) => {
    setSurveys(prev => prev.map(survey => 
      survey.id === id ? { ...survey, ...updates } : survey
    ));
  };

  const deleteSurvey = (id: string) => {
    setSurveys(prev => prev.filter(survey => survey.id !== id));
  };

  const getSurvey = (id: string) => {
    return surveys.find(survey => survey.id === id);
  };

  const addResponse = (surveyId: string, responseData: Omit<SurveyResponse, 'id' | 'submittedAt'>) => {
    const newResponse: SurveyResponse = {
      ...responseData,
      id: uuidv4(),
      submittedAt: new Date()
    };
    
    setSurveys(prev => prev.map(survey => 
      survey.id === surveyId 
        ? { ...survey, responses: [...(survey.responses || []), newResponse] }
        : survey
    ));
  };

  const getResponses = (surveyId: string): SurveyResponse[] => {
    const survey = getSurvey(surveyId);
    return survey?.responses || [];
  };

  return (
    <SurveyContext.Provider value={{
      surveys,
      addSurvey,
      updateSurvey,
      deleteSurvey,
      getSurvey,
      addResponse,
      getResponses
    }}>
      {children}
    </SurveyContext.Provider>
  );
};

export const useSurvey = () => {
  const context = useContext(SurveyContext);
  if (!context) {
    throw new Error('useSurvey must be used within a SurveyProvider');
  }
  return context;
};